import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import NotificationBell from "./NotificationBell";
import { 
  LayoutDashboard, 
  ShoppingBag,
  Users,
  Settings,
  LogOut,
  Calendar,
  FileText,
  Plus,
  Building2,
  ClipboardList,
  UserCog,
  Target,
  Globe,
  Image,
  Newspaper,
  Layers
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [orderCounts, setOrderCounts] = useState({});
  const [selectedMonth] = useState("January 2026");

  const fetchClients = async () => {
    try {
      const [clientsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/orders?month=${encodeURIComponent(selectedMonth)}`)
      ]);
      setClients(clientsRes.data);
      
      const counts = {};
      ordersRes.data.forEach(order => {
        counts[order.client_id] = (counts[order.client_id] || 0) + 1;
      });
      setOrderCounts(counts);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  useEffect(() => {
    // Only fetch clients if user has access to orders section
    if (user?.role === "admin" || user?.role === "content_manager") {
      fetchClients();
    }
  }, [user, selectedMonth]);

  // Pin body to viewport height while inside the authenticated layout so the
  // sidebar and main content each get their own scroll, and the whole page
  // never scrolls (public pages continue to work because this only runs here).
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const prevHtmlHeight = document.documentElement.style.height;
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
    document.body.style.overflow = "hidden";
    document.body.style.height = "100%";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.height = prevBodyHeight;
      document.documentElement.style.height = prevHtmlHeight;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "admin";
  const isContentManager = user?.role === "content_manager";
  const isSME = user?.role === "associate_sme" || user?.role === "sr_sme";
  const isBDE = user?.role === "bde";

  // Format role display name
  const formatRoleName = (role) => {
    const roleMap = {
      admin: "Admin",
      content_manager: "Content Manager",
      associate_sme: "Associate SME",
      sr_sme: "Sr. SME",
      bde: "BDE"
    };
    return roleMap[role] || role;
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="w-60 sidebar flex flex-col bg-white border-r border-slate-100 h-screen">
        {/* Logo */}
        <div className="p-4 border-b border-slate-100">
          <img 
            src="https://customer-assets.emergentagent.com/job_client-order-dash/artifacts/0yz21vmt_Picture%201.png" 
            alt="Prepify" 
            className="h-7"
            data-testid="logo"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {/* Main Navigation */}
          <div className="sidebar-section">Main</div>
          <NavLink
            to="/app"
            end
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={18} />
            Dashboard
          </NavLink>

          {/* Sales CRM - Admin & BDE */}
          {(isAdmin || isBDE) && (
            <NavLink
              to="/app/sales-crm"
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid="nav-sales-crm"
            >
              <Target size={18} />
              Sales CRM
            </NavLink>
          )}

          {/* Orders - Admin & Content Manager can view all orders */}
          {(isAdmin || isContentManager) && (
            <NavLink
              to="/app/all-orders"
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid="nav-orders"
            >
              <ShoppingBag size={18} />
              All Orders
              <span className="count">{Object.values(orderCounts).reduce((a, b) => a + b, 0)}</span>
            </NavLink>
          )}

          {/* Monthly Overview - Admin Only (has financials) */}
          {isAdmin && (
            <NavLink
              to="/app/monthly"
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid="nav-monthly"
            >
              <ClipboardList size={18} />
              Monthly Overview
            </NavLink>
          )}

          {/* Clients - Admin/Content Manager */}
          {(isAdmin || isContentManager) && clients.length > 0 && (
            <>
              <div className="sidebar-section mt-4">Clients</div>
              {clients.map((client) => (
                <NavLink
                  key={client.id}
                  to={`/app/clients/${client.id}`}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                  data-testid={`nav-client-${client.id}`}
                >
                  <Building2 size={18} />
                  <span className="truncate flex-1">{client.name}</span>
                  <span className="count">{orderCounts[client.id] || 0}</span>
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to="/app/clients"
                  className="sidebar-link text-prepify-600"
                  data-testid="nav-manage-clients"
                >
                  <Plus size={18} />
                  Manage Clients
                </NavLink>
              )}
            </>
          )}

          {/* Workspace Section - Everyone */}
          <div className="sidebar-section mt-4">Workspace</div>
          <NavLink
            to="/app/attendance"
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid="nav-attendance"
          >
            <Calendar size={18} />
            Attendance & Leave
          </NavLink>

          {/* Leave Approval - Admin Only */}
          {isAdmin && (
            <NavLink
              to="/app/leaves"
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid="nav-leaves"
            >
              <FileText size={18} />
              Leave Approvals
            </NavLink>
          )}

          {/* Admin Settings */}
          {isAdmin && (
            <>
              <div className="sidebar-section mt-4">Website (CMS)</div>
              <NavLink to="/app/website/pages" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-pages">
                <FileText size={18} />
                Pages
              </NavLink>
              <NavLink to="/app/website/homepage" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-homepage">
                <Layers size={18} />
                Homepage Editor
              </NavLink>
              <NavLink to="/app/website/navigation" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-navigation">
                <ClipboardList size={18} />
                Navigation
              </NavLink>
              <NavLink to="/app/website/settings" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-settings">
                <Globe size={18} />
                Site Settings
              </NavLink>
              <NavLink to="/app/website/media" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-media">
                <Image size={18} />
                Media Library
              </NavLink>
              <NavLink to="/app/website/logos" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-logos">
                <Building2 size={18} />
                Partner Logos
              </NavLink>
              <NavLink to="/app/website/blog" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-cms-blog">
                <Newspaper size={18} />
                Blog
              </NavLink>
              <NavLink to="/app/website/blog-categories" className={({ isActive }) => `sidebar-link sidebar-sublink ${isActive ? "active" : ""}`} data-testid="nav-cms-blog-categories">
                <span style={{ width: 18 }} />
                ↳ Categories
              </NavLink>
              <NavLink to="/app/website/blog-tags" className={({ isActive }) => `sidebar-link sidebar-sublink ${isActive ? "active" : ""}`} data-testid="nav-cms-blog-tags">
                <span style={{ width: 18 }} />
                ↳ Tags
              </NavLink>

              <div className="sidebar-section mt-4">Administration</div>
              <NavLink
                to="/app/users"
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid="nav-users"
              >
                <Users size={18} />
                User Management
              </NavLink>
              <NavLink
                to="/app/masterdata"
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid="nav-masterdata"
              >
                <Settings size={18} />
                Master Data
              </NavLink>
            </>
          )}

          {/* Account Settings - Everyone */}
          <div className="sidebar-section mt-4">Account</div>
          <NavLink
            to="/app/settings"
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid="nav-settings"
          >
            <UserCog size={18} />
            Settings
          </NavLink>
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-prepify-100 flex items-center justify-center">
              <span className="text-sm font-semibold text-prepify-600">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{formatRoleName(user?.role)}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full btn-secondary justify-center text-sm py-2"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto h-screen">
        {/* Top Bar with Notification Bell */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-slate-100 z-10">
          <div className="flex items-center justify-end px-6 py-3">
            <NotificationBell />
          </div>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
